import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./ProductAddonsPopupBundle.module.css";
import BundlePreviewWrapper, {
    CustomSwatches,
    SubscriptionSelector,
    Swatches,
    VariantSelector,
    VariantSelectorV2,
} from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import { ProductObj, ProductAddonsPopupConfigInterface, VariantsObj } from "../../Types";
import {
    getPriceLabel,
    getFinalPrice,
    getPixelsForImage,
    updateBundleConfigWithRealTimeProducts,
    getFbtProductRecommendations,
    buildMetafieldFromSwatchOptions,
} from "../../constants";
import { TStoreContext } from "../../../../context/types";
import ProductImageWrapper from "../../BundlePreviewWrapper/ProductImageWrapper";
import useIsMobile from "../../../../hooks/useIsMobile";
import useCustomLocation from "../../../../hooks/useCustomLocation";
import CloseIcon from "../../../../components/Icons/CloseIcon";
import { FbtAiData } from "../../FTB/FBTBundle/FbtAiData";
import { addBundleToCart } from "../../RenderOnStore/utilities";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getProductId } from "../../../constants";
import { useShopifyProductState } from "../../../../hooks/useShopifyProductState";

function isProductAvailable(product: ProductObj) {
    if (!product || !product.variants) {
        return false; // Return false if product or its variants are undefined/null
    }
    return product?.variants?.some(
        (variant) => (variant.quantityAvailable && variant.quantityAvailable > 0) || variant.availableForSale,
    );
}

// this is my popup modal
export const ProductAddonsPopupModal = ({ config }: { config: ProductAddonsPopupConfigInterface }) => {
    const [openMyModal, setOpenMyModal] = useState(window.isProductAddOnPopupModalOpen);

    useEffect(() => {
        const handleGlobalModalStateChange = () => {
            setOpenMyModal(window.isProductAddOnPopupModalOpen);
        };
        window.addEventListener("productAddOnPopupStateChange", handleGlobalModalStateChange);

        return () => {
            window.removeEventListener("productAddOnPopupStateChange", handleGlobalModalStateChange);
        };
    }, []);

    const closeProductAddonsPopupModal = () => {
        window.isProductAddOnPopupModalOpen = false;
        const event = new Event("productAddOnPopupStateChange");
        window.dispatchEvent(event);
    };

    if (!openMyModal) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={styles.custom_modal_overlay} onClick={closeProductAddonsPopupModal}>
            <div className={styles.custom_modal_content} onClick={(e) => e.stopPropagation()}>
                <ProductAddonsPopupBundle {...config} key={config.form_id} />
            </div>
        </div>,
        document.body,
    );
};

const ProductAddonsPopupBundle = (bundleConfig: ProductAddonsPopupConfigInterface) => {
    return (
        !!bundleConfig && (
            <ProductAddonsPopupPreview
                bundleConfig={bundleConfig}
                standAlone={true}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                    } as TStoreContext
                }
            />
        )
    );
};

type Props_ProductAddonsPopupPreview = {
    bundleConfig: ProductAddonsPopupConfigInterface;
    standAlone?: boolean;
    appConfigContext: TStoreContext;
    SCREEN?: string;
};

const ProductCardShimmer = ({ brandingVisible }: { brandingVisible: boolean }) => {
    typeof brandingVisible;
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "20px",
                color: "#999999",
                paddingRight: 10,
            }}
        >
            <span style={{ marginTop: 25, marginBottom: 25 }}>Select Add-on product(s)</span>
        </div>
    );
};

const AiProductCards = ({
    bundleConfig,
    appConfigContext,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
}: {
    bundleConfig: any;
    appConfigContext: TStoreContext;
    finalProductsList: any;
    setFinalProductsList: any;
    shouldDiscountApply: boolean;
    SCREEN?: string;
}) => {
    const { sft } = appConfigContext;
    const [fetchingAiFbt, setFetchingAiFbt] = useState(true);

    const arr = finalProductsList?.filter((rec: any, idx: number) => idx <= bundleConfig?.max_limit_of_recommendations);

    const getAiFbtData = async (_sft: string) => {
        try {
            setFetchingAiFbt(true);

            const initialData = await updateBundleConfigWithRealTimeProducts(_sft, [bundleConfig], false, 1);

            if (initialData?.length > 0 && initialData[0]?.products?.length) {
                const firstProductId = initialData[0].products[0].id;
                const fbtData = await getFbtProductRecommendations(_sft, firstProductId, bundleConfig, 6, false);

                await updateBundleConfigWithRealTimeProducts(_sft, [fbtData as any], false, null);

                const mainProducts = fbtData?.products || [];
                const offeredProducts = fbtData?.offered_products || [];

                if (mainProducts.length > 0 || offeredProducts.length > 0) {
                    const finalProducts = [...mainProducts, ...offeredProducts].map((product) => ({
                        ...product,
                        checked: isProductAvailable(product as any),
                        required_product: mainProducts.includes(product),
                        quantity: product?.quantity || 1,
                    }));

                    setFinalProductsList(finalProducts);
                    return;
                }
            }

            const fallbackProducts = (FbtAiData || []).map((product, idx) => ({
                ...product,
                checked: isProductAvailable(product as any),
                required_product: idx == 0 ? true : false,
            }));

            setFinalProductsList(fallbackProducts);
        } catch (error) {
            console.error("Error fetching AI FBT data:", error);
        } finally {
            setFetchingAiFbt(false);
        }
    };

    useEffect(() => {
        if (bundleConfig?.product_add_on_offered_products_type == "recommendations") {
            getAiFbtData(sft);
        }
    }, [bundleConfig?.product_add_on_offered_products_type, bundleConfig?.products]);

    return (
        <div
            className={styles.products}
            style={{
                flexWrap: "nowrap",
            }}
        >
            {fetchingAiFbt ? (
                <ProductCardShimmer brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi} />
            ) : (
                arr.map((product: any, index: number) => (
                    <ProductCard
                        key={product?.id}
                        index={index}
                        product={product}
                        bundleConfig={bundleConfig}
                        appConfigContext={appConfigContext}
                        standAlone={false}
                        setTrackVariant={() => {}}
                        isMobile={false}
                        setFinalProductsList={setFinalProductsList}
                        finalProductsList={finalProductsList}
                        isAiFbt={true}
                        shouldDiscountApply={shouldDiscountApply}
                        variants={product?.variants}
                    />
                ))
            )}
        </div>
    );
};

const ProductCard = ({
    product,
    index,
    bundleConfig,
    appConfigContext,
    standAlone = false,
    setTrackVariant = () => {},
    isMobile,
    finalProductsList,
    setFinalProductsList,
    shouldDiscountApply,
    variants,
}: {
    product: ProductObj;
    index: number;
    bundleConfig: ProductAddonsPopupConfigInterface;
    appConfigContext: TStoreContext;
    standAlone: boolean;
    setTrackVariant: any;
    variants: VariantsObj[];
    isMobile: boolean;
    setFinalProductsList: any;
    finalProductsList: any[] | [];
    isAiFbt?: boolean;
    shouldDiscountApply: boolean;
}) => {
    const [disPrice, setDisPrice] = useState(0);
    const [origPrice, setOrigPrice] = useState(0);
    const { variantId, quantity } = useShopifyProductState(standAlone && product.required_product);

    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>([]);
    const { currency, money_format } = appConfigContext;

    useEffect(() => {
        setSelectedVariants(
            new Array(quantity)
                ?.fill(
                    product?.variants?.find((vv) =>
                        product.required_product ? vv.id.split("/").pop() == variantId : vv.availableForSale == true,
                    ) || product?.variants?.[0],
                )
                ?.map((variant) => ({
                    ...variant,
                    selling_plan: !product.oneTimePurchase
                        ? product.subscriptions && product?.subscriptions.find((sub) => sub.checked === true)?.id
                        : "",
                    copyPrice: variant?.price,
                })),
        );
    }, [variantId, quantity]);

    useEffect(() => {
        if (product?.variants?.length) {
            let disP = +product.variants[0].price * product?.quantity;
            const origP = +product.variants[0].price * product?.quantity;

            // Apply discount only if the product is NOT a required product
            if (!product.required_product) {
                const dis = bundleConfig?.discount_value || 0;
                switch (bundleConfig?.discount_type) {
                    case "percentage":
                        disP = disP - disP * (dis / 100);
                        break;
                    case "flat":
                        disP = disP - dis;
                        break;
                    default:
                }
            }

            setDisPrice(Math.round(disP * 100) / 100);
            setOrigPrice(Math.round(origP * 100) / 100);
        } else {
            setDisPrice(0);
            setOrigPrice(0);
        }
    }, [product, bundleConfig?.discount_type, bundleConfig?.discount_value]);

    useEffect(() => {
        // if (product.checked) {
        // Add productIdx to each selectedVariant
        const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
            ...variant,
            productIdx: index, // Add the product index
        }));
        product.selectedVariants = updatedSelectedVariants; // Assign updated variants to the product

        // Update trackVariant state
        setTrackVariant((prevVariants: any[]) => {
            const updatedVariants = [...prevVariants];
            // Check if product ID already exists in the array
            // @ts-expect-error required
            const productExists = prevVariants.some((variant) => variant?.id === updatedSelectedVariants.id);

            if (!productExists) {
                updatedVariants[index] = updatedSelectedVariants; // Update only if product doesn't exist
            }
            return updatedVariants;
        });

        let totalOriginalPrice = 0;
        let totalDiscountedPrice = 0;

        // Calculate total original price
        updatedSelectedVariants.forEach((variant: any) => {
            // if (product.checked) {
            const variantPrice = bundleConfig?.use_compare_at_price ? +variant?.compareAtPrice : +variant?.price;
            totalOriginalPrice += variantPrice;
            // }
        });
        // Apply discount based on bundleConfig after summing the prices
        totalDiscountedPrice = totalOriginalPrice;
        const discountValue = bundleConfig?.discount_value || 0;

        switch (bundleConfig?.discount_type) {
            case "percentage":
                totalDiscountedPrice = totalOriginalPrice - totalOriginalPrice * (discountValue / 100);
                break;
            case "flat":
                totalDiscountedPrice = totalOriginalPrice - discountValue;
                break;
            default:
                break;
        }

        // Set the final calculated prices, rounded to 2 decimal places
        setDisPrice(Math.round(totalDiscountedPrice * 100) / 100);
        setOrigPrice(Math.round(totalOriginalPrice * 100) / 100);
        // }
    }, [selectedVariants, product, bundleConfig, index]);

    const handleProductClick = (e: any) => {
        e?.preventDefault();
        if (standAlone && !window.location.pathname.includes(product.handle)) {
            window.open(`https://${window?.location?.host}/products/${product.handle}`, "_blank");
        } else if (!window.location.pathname.includes(product.handle)) {
            window.open(`https://${shopify?.config?.shop}/products/${product.handle}`, "_blank");
        }
    };

    useEffect(() => {
        if (product.subscriptions && product.subscriptions.length > 0 && !product.oneTimePurchase) {
            product.isSubscribed = true;
        }
    }, [product, product.oneTimePurchase]);

    const isProductAvailable = useMemo(() => {
        return product?.variants?.some(
            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [product]);

    const getPrice = (type: any, price: any) => {
        const finalPrice = getFinalPrice(type, 0, price);
        return finalPrice <= 0 ? bundleConfig?.free_text || "Free" : getPriceLabel(finalPrice, money_format, currency);
    };

    const getNewPriceLabel = () => {
        const discountedPrice = getFinalPrice("none", 0, disPrice);
        const compareAtPrice = getFinalPrice(
            "none",
            0,
            bundleConfig.use_compare_at_price
                ? selectedVariants.reduce((total, sv) => total + +sv.compareAtPrice, 0)
                : origPrice,
        );

        if (discountedPrice < compareAtPrice) {
            return getPriceLabel(compareAtPrice, money_format, currency);
        }
        // if (!product.checked) {
        //     return null;
        // }
        return null;
    };

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;

    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const renderPrice = (
        <div
            style={{
                width: "max-content",
                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "row",
                alignItems: "center",
                gap: 3,
                position: "relative",
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                <div
                    style={{
                        width: "max-content",
                    }}
                >
                    {getPrice("none", product.required_product ? origPrice : disPrice)}
                </div>
            </InspectorEditableBlock>
            {!product.required_product && (
                <>
                    {bundleConfig.use_compare_at_price ||
                    bundleConfig.discount_type === "flat" ||
                    bundleConfig.discount_type === "percentage" ? (
                        <InspectorEditableBlock
                            bundleType={bundleConfig?.bundle_type}
                            settingKey="productCompareAtPrice"
                        >
                            <div
                                style={{
                                    textDecoration: "line-through",
                                    fontSize: bundleConfig?.bundles_design.product_price_size,
                                    color: bundleConfig?.bundles_design.product_price_color,
                                    fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                                    width: "max-content",
                                    lineHeight: "normal",
                                }}
                            >
                                {getNewPriceLabel()}
                            </div>
                        </InspectorEditableBlock>
                    ) : (
                        ""
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {!product?.required_product && (
                <InspectorEditableBlock
                    bundleType={bundleConfig?.bundle_type}
                    settingKey="productCard"
                    tagsKey={product?.tags?.toString()}
                    productId={getProductId(product?.id)}
                >
                    <div
                        className={styles.productWrapper}
                        style={{
                            border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            backgroundColor: bundleConfig?.bundles_design.product_background_color,
                            width: "100%",
                            height: "auto",
                        }}
                    >
                        <div className={styles.productInfoRight}>
                            {bundleConfig?.fbt_show_checkboxes == 1 && (
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productCheckbox"
                                >
                                    <input
                                        id={styles.fbtInputCheckbox}
                                        style={{
                                            accentColor: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                        }}
                                        type={"checkbox"}
                                        disabled={!isProductAvailable}
                                        key={product.id}
                                        name={product.title}
                                        value={product.id}
                                        onChange={(e) => {
                                            console._log(e.target.value);
                                            const currentCheck = e.currentTarget.checked;
                                            const currentVal = e.target.value;
                                            setFinalProductsList((ps: ProductObj[]) => {
                                                return ps.map((fpl: any) => {
                                                    if (fpl.id == currentVal) {
                                                        return {
                                                            ...fpl,
                                                            checked: currentCheck,
                                                        };
                                                    } else {
                                                        return fpl;
                                                    }
                                                });
                                            });
                                        }}
                                        checked={
                                            !!finalProductsList?.find((fpl: ProductObj) => fpl?.id == product?.id)
                                                ?.checked
                                        }
                                    />
                                </InspectorEditableBlock>
                            )}
                            <ProductImageWrapper
                                product={product}
                                handleProductClick={handleProductClick}
                                bundleConfig={{
                                    ...bundleConfig,
                                    bundles_design: {
                                        ...bundleConfig?.bundles_design,
                                        product_img_size: bundleConfig?.bundles_design.product_img_size
                                            ? bundleConfig?.bundles_design.product_img_size
                                            : "small",
                                    },
                                }}
                                isMobile={isMobile}
                                selectedVariants={selectedVariants}
                            />
                            <div
                                className={styles.productMetaRight}
                                style={{
                                    color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                    fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                    gap: isMobile
                                        ? +getPixelsForImage(bundleConfig?.bundles_design?.product_img_size, "number") /
                                              10 +
                                          "px"
                                        : "0px",
                                    minHeight: +getPixelsForImage(
                                        bundleConfig?.bundles_design?.product_img_size,
                                        "number",
                                    ),
                                    whiteSpace: "normal" /* Allows text to wrap onto the next line */,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: bundleConfig?.bundles_design.product_name_size,
                                        color: bundleConfig?.bundles_design?.product_name_color,
                                        fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                        cursor: "pointer",
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        gap: isMobile ? 4 : 13,
                                    }}
                                    onClick={handleProductClick}
                                >
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productTitle"
                                        wrapWithDiv={true}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                justifyContent: isMobile ? "space-between" : "flex-start",
                                                gap: isMobile ? 4 : 8,
                                                marginRight: "15px",
                                            }}
                                        >
                                            {product?.title}
                                        </div>
                                    </InspectorEditableBlock>
                                    {
                                        <>
                                            {isProductAvailable ? (
                                                renderPrice
                                            ) : (
                                                <span
                                                    style={{
                                                        fontSize: "12px",
                                                        color: "red",
                                                    }}
                                                >
                                                    {bundleConfig?.bundles_settings?.out_of_stock_text}
                                                </span>
                                            )}
                                        </>
                                    }
                                </div>
                                {/* {isMobile && (
                                <ProductSubscriptionsPreview
                                    product={product}
                                    setSelectedVariants={setSelectedVariants}
                                    key={product.id}
                                    bundleConfig={bundleConfig}
                                />
                            )} */}
                            </div>
                        </div>
                        {product.variants.length > 1 && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    width: "100%",
                                }}
                            >
                                {selectedVariants.map((_: any, i: number) =>
                                    userShopname === "typsy-beauty.myshopify.com" ? (
                                        <Swatches
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            key={i}
                                            variants={product.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            options={product.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                        />
                                    ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                        <CustomSwatches
                                            key={i}
                                            variants={product.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            options={product.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            metafield={effectiveMetafield}
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            bundleConfig={bundleConfig}
                                        />
                                    ) : (
                                        <VariantSelectorV2
                                            key={i}
                                            variants={product.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            options={product.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            metafield={effectiveMetafield}
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            bundleConfig={bundleConfig}
                                        />
                                    ),
                                )}
                            </div>
                        )}

                        {product.isSubscribed && (
                            <div style={{ width: "100%" }}>
                                <div style={{ margin: "5px 0" }}>
                                    <span className={styles.separator}></span>
                                </div>

                                <SubscriptionSelector
                                    product={product}
                                    selectedVariants={selectedVariants}
                                    setSelectedVariants={setSelectedVariants}
                                />
                            </div>
                        )}
                    </div>
                </InspectorEditableBlock>
            )}
        </>
    );
};

export const ProductAddonsPopupPreview = ({
    bundleConfig,
    standAlone,
    appConfigContext,
    SCREEN = "",
}: Props_ProductAddonsPopupPreview) => {
    const [totalPrice, setTotalPrice] = useState(0);
    const [compareAtPrice, setCompareAtPrice] = useState(0);
    const { currency, money_format } = appConfigContext;
    const [trackVariant, setTrackVariant] = useState<any[]>([]);
    const bundleRef = useRef(null);
    const { isMobile } = useIsMobile();
    const [shouldDiscountApply, setShouldDiscountApply] = useState(true);
    const [finalProductsList, setFinalProductsList] = useState<any[] | []>([]);
    const [actionInProgress, setActionInProgress] = useState(false);
    useEffect(() => {
        if (finalProductsList) {
            let totalPrice = 0;
            let compPrice = 0;
            // Loop through all selected variants from each product in trackVariant
            trackVariant.forEach((productVariants) => {
                if (productVariants && productVariants.length) {
                    productVariants.forEach((variant: any) => {
                        if (variant && variant.price) {
                            totalPrice += +variant.price; // Add each variant's price to the total
                        }
                        if (variant && variant.compareAtPrice) {
                            compPrice += +variant.compareAtPrice;
                        }
                    });
                }
            });

            // Set the final total price
            setTotalPrice(totalPrice);
            setCompareAtPrice(compPrice);
        }
    }, [trackVariant, finalProductsList, bundleConfig?.discount_type, bundleConfig?.discount_value]);

    useEffect(() => {
        const temp: any[] = [];
        let isRequiredChecked = false;
        let isNonRequiredChecked = false;
        finalProductsList?.forEach((pd, idx: number) => {
            if (idx === 0 && !standAlone) {
                window.localStorage.setItem("addons_popup_handle", pd.handle);
            }
            if (pd.required_product && pd.checked) {
                isRequiredChecked = true;
            }
            if (!pd.required_product && pd.checked) {
                isNonRequiredChecked = true;
            }
            if (pd.checked) {
                temp.push(pd.selectedVariants);
            }
        });
        typeof isRequiredChecked;
        setShouldDiscountApply(isNonRequiredChecked);
        setTrackVariant(temp);
    }, [finalProductsList, standAlone]);

    useEffect(() => {
        if (
            (bundleConfig?.product_add_on_selection !== "all" && (bundleConfig?.products?.length || 0) > 0) ||
            (bundleConfig?.offered_products?.length || 0) > 0
        ) {
            const arr = [
                ...(bundleConfig?.products?.slice(0, 1)?.map((obj) => ({
                    ...obj,
                    checked: isProductAvailable(obj),
                    required_product: true,
                    quantity: +document.querySelector('[name="quantity"]')?.value || obj?.quantity || 1,
                })) || []),
                ...(bundleConfig?.offered_products?.map((obj) => ({
                    ...obj,
                    checked: isProductAvailable(obj),
                    required_product: false,
                    quantity: obj?.quantity || 1,
                })) || []),
            ];
            setFinalProductsList(arr);
        } else if (
            bundleConfig?.product_add_on_selection == "manual" ||
            bundleConfig?.product_add_on_selection == "collection"
        ) {
            setFinalProductsList([]);
        }
    }, [bundleConfig.products, bundleConfig?.offered_products, bundleConfig?.product_add_on_selection]);

    if (!bundleConfig) return null;
    const updatedBundleConfig = {
        ...bundleConfig,
        products: finalProductsList.filter((fpl) => !!fpl.checked),
        discount_type: shouldDiscountApply ? bundleConfig?.discount_type : "none",
        discount_value: shouldDiscountApply ? bundleConfig?.discount_value : "0",
    };

    return (
        <div
            style={{
                zoom: standAlone ? 1 : 0.8,
                overflow: standAlone ? "hidden" : "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: standAlone ? "flex-start" : "center",
                borderRadius: "8px",
            }}
        >
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="popupHeading">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "24px",
                        backgroundColor: bundleConfig?.bundles_design?.popup_heading_bg_color,
                        padding: "16px",
                        paddingInline: 18,
                        lineHeight: "normal",
                        borderWidth: "2px",
                        borderStyle: "solid",
                        borderColor: bundleConfig?.bundles_design?.popup_heading_color || "#000000",
                        borderRadius: "16px 16px 0px 0px",
                    }}
                >
                    <span
                        style={{
                            fontSize: bundleConfig?.bundles_design?.popup_heading_size,
                            color: bundleConfig?.bundles_design?.popup_heading_color,
                            fontWeight: bundleConfig?.bundles_design?.popup_heading_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.popup_heading_style?.split(" ")[1],
                            lineHeight: "normal",
                            display: "contents",
                            fontFamily:
                                bundleConfig?.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                    ? "inherit"
                                    : bundleConfig?.bundles_design?.bundle_widget_font_family,
                        }}
                    >
                        {bundleConfig?.popup_heading}
                    </span>

                    <button
                        style={{
                            all: "unset",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: bundleConfig?.bundles_design?.popup_heading_color || "ffffff",
                            cursor: "pointer",
                            fontSize: "18px",
                        }}
                        disabled={actionInProgress}
                        onClick={async () => {
                            setActionInProgress(true);
                            await addBundleToCart(
                                [finalProductsList.find((product) => product.required_product)],
                                {},
                                bundleConfig.form_id || "",
                                !!bundleConfig?.general_settings?.redirect_to_checkout,
                                bundleConfig?.bundle_type,
                            );

                            window.isProductAddOnPopupModalOpen = false;
                            const event = new Event("productAddOnPopupStateChange");
                            window.dispatchEvent(event);
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
            </InspectorEditableBlock>

            <div
                style={{
                    padding: "8px 18px",
                    maxHeight: "70vh",
                    overflowY: "auto",
                    background: bundleConfig?.bundles_design?.bundle_widget_bg,
                }}
            >
                <BundlePreviewWrapper
                    bundleConfig={updatedBundleConfig as any}
                    standAlone={standAlone}
                    currency={currency}
                    money_format={money_format}
                    totalPrice={totalPrice}
                    compareAtPriceTotal={compareAtPrice}
                    otherLoading={actionInProgress}
                >
                    {!standAlone && bundleConfig?.product_add_on_offered_products_type == "recommendations" ? (
                        <>
                            <AiProductCards
                                bundleConfig={{
                                    ...bundleConfig,
                                    form_id: bundleConfig?.form_id || "123",
                                }}
                                appConfigContext={appConfigContext}
                                finalProductsList={finalProductsList}
                                shouldDiscountApply={shouldDiscountApply}
                                setFinalProductsList={setFinalProductsList}
                                SCREEN={SCREEN}
                            />
                        </>
                    ) : finalProductsList?.length ? (
                        <div className={`${styles.products} ${standAlone && styles.productsOverflow}`} ref={bundleRef}>
                            {finalProductsList?.map((product, index) => {
                                return (
                                    <ProductCard
                                        key={product?.id + product?.variants[0]?.id}
                                        index={index}
                                        product={product}
                                        bundleConfig={bundleConfig}
                                        appConfigContext={appConfigContext}
                                        standAlone={!!standAlone}
                                        setTrackVariant={setTrackVariant}
                                        isMobile={isMobile}
                                        setFinalProductsList={setFinalProductsList}
                                        finalProductsList={finalProductsList}
                                        shouldDiscountApply={shouldDiscountApply}
                                        variants={product?.variants}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <ProductCardShimmer brandingVisible={!!bundleConfig?.general_settings?.show_powered_by_kefi} />
                    )}
                </BundlePreviewWrapper>
            </div>
        </div>
    );
};

export default ProductAddonsPopupBundle;
